<template>
  <div class="device-calibration">
    <component
      v-for="field in calibrationFields"
      :key="field.dataCy"
      :is="field.componentName"
      customClass="small-subtitle"
      v-bind="field"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PopoverTimestamp from '@/components/devicePopover/content/popoverTimestamp.vue'
import PopoverNumber from '@/components/devicePopover/content/popoverNumber.vue'
import PopoverText from '@/components/devicePopover/content/popoverText.vue'
import { activeLastDay } from '@/services/device-status'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'

type ValueType = string | number | null

interface CalibrationField {
  componentName: string
  value: ValueType
  label: string
  dataCy: string
  invalidText?: string
  showTimeZoneDate?: boolean
  precision?: number
}

export default defineComponent({
  name: 'DeviceCalibration',
  components: {
    PopoverTimestamp,
    PopoverNumber,
    PopoverText
  },
  props: {
    deviceId: String
  },
  computed: {
    isTelemetryActive(): boolean {
      const timestamp = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_TELEMETRY_TIMESTAMP_BY_DEVICE_ID}`
      ](this.deviceId)
      return activeLastDay(timestamp)
    },
    calibrationFields(): CalibrationField[] {
      const calibrationName = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CALIBRATION_NAME_BY_DEVICE_ID}`
      ](this.deviceId)
      const photometricCalFactor = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_PHOTOMETRIC_CAL_FACTOR_BY_DEVICE_ID}`
      ](this.deviceId)
      const calibrationDate = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CALIBRATION_DATE_BY_DEVICE_ID}`
      ](this.deviceId)
      const sizeCalFactor = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SIZE_CAL_FACTOR_BY_DEVICE_ID}`
      ](this.deviceId)
      const flowCalibrationFactor = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_FLOW_CALIBRATION_FACTOR_BY_DEVICE_ID}`
      ](this.deviceId)

      return [
        {
          componentName: 'PopoverText',
          value: this.getFormattedValue(calibrationName),
          label: this.$t('widget.currentReadings.calName'),
          invalidText: this.$t('map.popover.valueBlank'),
          dataCy: 'calName'
        },
        {
          componentName: 'PopoverNumber',
          value: this.getFormattedValue(photometricCalFactor),
          label: this.$t('widget.currentReadings.photometricCalFactor'),
          dataCy: 'photometricCalFactor',
          precision: 3
        },
        {
          componentName: 'PopoverTimestamp',
          value: this.getFormattedValue(calibrationDate),
          label: this.$t('widget.currentReadings.calibrationDate'),
          invalidText: this.$t('map.popover.valueBlank'),
          showTimeZoneDate: true,
          dataCy: 'calibrationDate'
        },
        {
          componentName: 'PopoverNumber',
          value: this.getFormattedValue(sizeCalFactor),
          label: this.$t('widget.currentReadings.sizeCalFactor'),
          dataCy: 'sizeCalFactor',
          precision: 3
        },
        {
          componentName: 'PopoverNumber',
          value: this.getFormattedValue(flowCalibrationFactor),
          label: this.$t('widget.currentReadings.flowCalibrationFactor'),
          dataCy: 'flowCalibrationFactor',
          precision: 2
        }
      ]
    }
  },
  methods: {
    getFormattedValue(value: ValueType): ValueType {
      return this.isTelemetryActive ? value : null
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/global-variables' as *;

.device-calibration {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  .small-subtitle {
    padding: 0;
    line-height: 1.4;
    min-width: 35%;
  }

  .small-subtitle:nth-child(odd) {
    padding-left: 12px;
  }

  .small-subtitle:nth-child(even) {
    text-align: end;
    padding-right: 12px;
  }

  .small-subtitle:last-child {
    width: 100%;
    padding: 0;
    background-color: rgba($great-light-shade, 0.6);
    text-align: center;
  }
}
</style>
